import type {
  ProgramDisability,
  ProgramLanguage,
  ProgramReservationState,
  ProgramSession,
} from "@/types/api/program"
import {
  DAY_MS, alignToToday, combineDateTime, toMfmProgramTs,
} from "@/utils/date"

export interface ProgramCardSpeaker {
  id: string
  name: string
  role: string
  photoUrl: string
}

export interface ProgramCardDirection {
  id: string
  title: string
}

export interface ProgramDay {
  value: string
  ts: number
}

export interface ProgramCardBadge {
  icon: string
  labelKey: string
}

export type ProgramCardState =
  | "none"
  | "open"
  | "enrolled"
  | "waiting"
  | "closed"

export type ProgramSeatsState = "absent" | "open" | "full"

export interface ProgramCardPartner {
  logo: string
  name: string
}

export interface ProgramCardSlot {
  id: string
  start: number
  finish: number
  seatsLeft: number
  seatsMgnLeft: number
}

export interface ProgramCard {
  id: string
  title: string
  description: string
  cover: string
  start: number
  end: number
  languages: string[]
  language: string
  badges: ProgramCardBadge[]
  broadcastUrl: string
  hallId: string
  hallTitle: string
  blockId: string
  blockTitle: string
  directions: ProgramCardDirection[]
  speakers: ProgramCardSpeaker[]
  partnerLogo: string
  partnerName: string
  partners: ProgramCardPartner[]
  seatsTotal: number
  seatsTaken: number
  seatsRegularTotal: number
  seatsRegularTaken: number
  seatsMgnTotal: number
  seatsMgnTaken: number
  isFavorite: boolean
  isVisibleOnLanding: boolean
  enrollFrom: number
  enrollTo: number
  state: ProgramCardState
  regularState: ProgramSeatsState
  mgnState: ProgramSeatsState
  isEnrolledMgn: boolean
  canCancel: boolean
  reservationStart: number
  reservationFinish: number
  slots: ProgramCardSlot[]
}

const LANGUAGES: Record<ProgramLanguage, string[]> = {
  RUSSIAN: ["ru"],
  ENGLISH: ["en"],
  RUSSIAN_WITH_ENGLISH_TRANSLATION: ["ru", "en"],
  ENGLISH_WITH_RUSSIAN_TRANSLATION: ["en", "ru"],
  RUSSIAN_WITH_CONSECUTIVE_ENGLISH_TRANSLATION: ["ru", "en"],
  ENGLISH_WITH_CONSECUTIVE_RUSSIAN_TRANSLATION: ["en", "ru"],
  OTHER_WITH_RUSSIAN_TRANSLATION: ["oth", "ru"],
  OTHER_WITH_ENGLISH_TRANSLATION: ["oth", "en"],
}

const DISABILITIES: Record<ProgramDisability, ProgramCardBadge> = {
  MOVEMENT: {
    icon: "wheelchair", labelKey: "programSlot.disabilityMovement",
  },
  VISION: {
    icon: "eye-slash", labelKey: "programSlot.disabilityVision",
  },
  HEARING: {
    icon: "hearing-impaired", labelKey: "programSlot.disabilityHearing",
  },
}

export const BROADCAST_LEAD_MS = 15 * 60 * 1000

export const LANDING_PROGRAM_FILTERS = [
  { name: "visibleOnLanding", value: true },
]

export const toReservationRange = (session: ProgramSession) => {
  const anchor = session.date

  return {
    start: session.reservationStartDate || session.reservationStart
      ? toMfmProgramTs(combineDateTime(
        session.reservationStartDate || alignToToday(anchor),
        session.reservationStart || null,
        anchor,
      ))
      : 0,

    finish: toMfmProgramTs(combineDateTime(
      session.reservationFinishDate || anchor,
      session.reservationFinish || session.start,
      anchor,
    )),
  }
}

export const toCardState = (session: ProgramSession): ProgramCardState => {
  if (session.type === "WITHOUT_RESERVATION") return "none"

  if (session.attendee?.state === "ACTIVE") return "enrolled"
  if (session.attendee?.state === "WAITING_LIST") return "waiting"

  return session.reservationEarlyOrFinished ? "closed" : "open"
}

export const toSeatsState = (
  total: number,
  state?: ProgramReservationState,
): ProgramSeatsState => {
  if (!total) return "absent"

  return state === "OPEN" ? "open" : "full"
}

const joinNotEmpty = (parts: string[], separator = " ") =>
  parts.filter(Boolean).join(separator)

export const toTs = (value?: Nullable<number | string>) => {
  if (!value) return 0

  const ts = typeof value === "number" ? value : Date.parse(value)
  return Number.isNaN(ts) ? 0 : ts
}

export const toProgramCard = (
  session: ProgramSession,
  locale = "ru",
): ProgramCard => {
  const partner = session.partners?.[0]?.partner
  const logo = locale === "en" ? partner?.logoEn : partner?.logoRu
  const reservation = toReservationRange(session)

  const start = toTs(session.startDate)
  const finish = toTs(session.finishDate)
  const end = start && finish && finish <= start ? finish + DAY_MS : finish

  const capacityRegular = session.totalCapacity || 0
  const capacityMgn = session.totalCapacityMGN || 0
  const reservedRegular = session.reserved || 0
  const reservedMgn = session.reservedMGN || 0
  const seatsTaken = capacityRegular && capacityMgn
    ? reservedRegular + reservedMgn
    : capacityMgn ? reservedMgn : reservedRegular

  return {
    id: session.id,
    title: session.title?.current || "",
    description: session.shortDescription?.current || "",
    cover: session.photo?.url || "",

    start,
    end,

    languages: session.language ? LANGUAGES[session.language] || [] : [],
    language: session.language || "",

    badges: (session.allowedDisabilities || [])
      .map(item => DISABILITIES[item])
      .filter(Boolean),

    broadcastUrl: session.streamURL?.current || "",

    hallId: session.hall?.id || "",
    hallTitle: session.hall?.title?.current || "",
    blockId: session.block?.id || "",
    blockTitle: session.block?.title?.current || "",

    seatsTotal: capacityRegular + capacityMgn,
    seatsTaken,
    seatsRegularTotal: capacityRegular,
    seatsRegularTaken: reservedRegular,
    seatsMgnTotal: capacityMgn,
    seatsMgnTaken: reservedMgn,

    isFavorite: !!session.favorite,
    isVisibleOnLanding: session.visibleOnLanding !== false,
    enrollFrom: toTs(session.attendee?.slot?.start),
    enrollTo: toTs(session.attendee?.slot?.finish),

    state: toCardState(session),
    regularState: toSeatsState(capacityRegular, session.reservationState),
    mgnState: toSeatsState(capacityMgn, session.reservationStateMGN),
    isEnrolledMgn: !!session.attendee?.mgn,
    canCancel: session.attendee?.state === "ACTIVE",
    reservationStart: reservation.start,
    reservationFinish: reservation.finish,

    slots: (session.slots || []).map(slot => ({
      id: slot.id,
      start: toTs(slot.start),
      finish: toTs(slot.finish),
      seatsLeft: Math.max(0, (session.capacity || 0) - (slot.reserved || 0)),
      seatsMgnLeft: Math.max(
        0, (session.capacityMGN || 0) - (slot.reservedMGN || 0),
      ),
    })),

    directions: (session.directions || []).map(item => ({
      id: item.id,
      title: item.title.current,
    })),

    speakers: (session.participants || [])
      .map(item => ({
        id: item.id,
        name: joinNotEmpty([
          item.person.firstName?.current,
          item.person.lastName?.current,
        ]),
        role: joinNotEmpty([
          item.person.position?.current,
          item.person.company?.current,
        ], ", "),
        photoUrl: item.person.photo?.url || "",
      })),

    partnerLogo: logo?.url || "",
    partnerName: partner?.title.current || "",

    partners: (session.partners || [])
      .map(item => ({
        logo: (locale === "en"
          ? item.partner?.logoEn
          : item.partner?.logoRu)?.url || "",
        name: item.partner?.title.current || "",
      }))
      .filter(item => item.logo || item.name),
  }
}

export const toDaySlots = (days: ProgramDay[]) =>
  days.map(day => ({ start: toTs(day.ts) }))

export const toOptions = (items: { id: string; title: Localized }[]) => {
  const options: Record<string, string> = {}

  for (const item of items)
    if (item.title.current) options[item.title.current] = item.id

  return options
}
